/**
 * PATH: packages/api/src/blobDownload.ts
 * FILE_ID: API-BLOB-DOWNLOAD-001
 * PURPOSE: Safely download authenticated blob responses with server filenames.
 * WHY: Every caller must revoke object URLs and treat response filenames consistently.
 */

export interface SaveBlobDownloadOptions {
  blob: Blob
  fallbackFilename: string
  contentDisposition?: string | null
}

function decodeFilename(value: string): string {
  try {
    return decodeURIComponent(value)
  } catch {
    return value
  }
}

function safeFilename(value: string): string {
  const leaf = value.replace(/\\/g, '/').split('/').pop()?.trim() ?? ''
  return leaf.replace(/[\u0000-\u001f\u007f]/g, '') || 'download'
}

export function filenameFromContentDisposition(
  contentDisposition: string | null | undefined,
  fallbackFilename: string,
): string {
  if (!contentDisposition) return safeFilename(fallbackFilename)

  const encoded = contentDisposition.match(
    /filename\*\s*=\s*(?:UTF-8'')?([^;]+)/i,
  )?.[1]
  if (encoded) {
    return safeFilename(decodeFilename(encoded.trim().replace(/^["']|["']$/g, '')))
  }

  const plain = contentDisposition.match(
    /filename\s*=\s*(?:"([^"]+)"|'([^']+)'|([^;]+))/i,
  )
  const filename = plain?.[1] ?? plain?.[2] ?? plain?.[3]
  return safeFilename(filename?.trim() || fallbackFilename)
}

export function saveBlobDownload({
  blob,
  fallbackFilename,
  contentDisposition,
}: SaveBlobDownloadOptions): string {
  const filename = filenameFromContentDisposition(
    contentDisposition,
    fallbackFilename,
  )
  const objectUrl = URL.createObjectURL(blob)
  const anchor = document.createElement('a')

  try {
    anchor.href = objectUrl
    anchor.download = filename
    anchor.style.display = 'none'
    document.body.appendChild(anchor)
    anchor.click()
  } finally {
    anchor.remove()
    URL.revokeObjectURL(objectUrl)
  }

  return filename
}
